// useClientSideFilters.ts
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSearchStore } from '~/store/search'

// Define filter helper functions directly in this file
const hasValidFreeCancellation = (rate: any) => {
  if (!rate?.is_refundable) {
    return false
  }
  
  // Check if cancellation window hasn't expired
  if (rate.cancellation_policies?.length) {
    return rate.cancellation_policies.some((policy: any) => {
      const deadline = new Date(policy.dt_deadline)
      const now = new Date()
      return deadline > now
    })
  }
  
  return rate.is_refundable
}

const matchesPaymentPreference = (rate: any, payAtPropertyOnly: boolean, payNowOnly: boolean) => {
  if (!payAtPropertyOnly && !payNowOnly) {
    return true // Show all if no specific preference
  }
  
  const hasPayNowAmount = rate.pay_now_total?.amount && parseFloat(rate.pay_now_total.amount) > 0
  const hasCheckInAmount = rate.check_in_amount?.amount && parseFloat(rate.check_in_amount.amount) > 0
  
  if (payAtPropertyOnly) {
    // Show only rates where full payment is at check-in (no pay now amount)
    return !hasPayNowAmount && hasCheckInAmount
  }
  
  if (payNowOnly) {
    // Show only rates where payment is required now
    return hasPayNowAmount
  }
  
  return true
}

const meetsQualityRequirements = (hotel: any, minRating: number, minReviews: number) => {
  const hotelRating = hotel.avg_rating || 0
  const hotelReviews = hotel.reviews_count || 0
  
  const meetsRating = minRating === 0 || hotelRating >= minRating
  const meetsReviews = minReviews === 0 || hotelReviews >= minReviews
  
  return meetsRating && meetsReviews
}

// Haversine formula to calculate distance between two coordinates in km
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371 // Radius of the Earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

const matchesLocationPreference = (hotel: any, distanceBand: string, neighborhoods: string[], customLocation: any = null) => {
  // Check distance band (only applies when custom location is set)
  if (distanceBand && customLocation) {
    const maxDistance = parseFloat(distanceBand)
    
    // Get hotel coordinates
    const hotelLat = hotel.hotel?.latitude || hotel.hotel?.lat || hotel.latitude || hotel.lat
    const hotelLng = hotel.hotel?.longitude || hotel.hotel?.lng || hotel.longitude || hotel.lng
    
    // Get custom location coordinates
    const customLat = customLocation.lat
    const customLng = customLocation.lng
    
    if (hotelLat && hotelLng && customLat && customLng) {
      const distance = calculateDistance(hotelLat, hotelLng, customLat, customLng)
      
      if (distance > maxDistance) {
        return false
      }
    } else {
      // If we don't have coordinates, we can't filter by distance
      // Could fall back to excluding the hotel, but let's be permissive
      console.warn('Missing coordinates for distance filtering', { hotel: hotel.hotel?.name, hotelLat, hotelLng, customLat, customLng })
    }
  }

  // Check neighborhoods
  if (neighborhoods.length > 0) {
    const hotelNeighborhoods = [
      hotel.neighborhood,
      hotel.district,
      hotel.area,
      hotel.location?.neighborhood,
      hotel.location?.district,
      hotel.address?.district,
      hotel.geo?.neighborhood
    ].filter(Boolean).map((n: string) => n.trim())
    
    const hasMatchingNeighborhood = neighborhoods.some(selected =>
      hotelNeighborhoods.some((hotelN: string) => 
        hotelN.toLowerCase().includes(selected.toLowerCase()) ||
        selected.toLowerCase().includes(hotelN.toLowerCase())
      )
    )
    
    if (!hasMatchingNeighborhood) {
      return false
    }
  }
  
  return true
}

const hasRequiredAmenities = (hotel: any, requiredCodes: number[]) => {
  if (!requiredCodes.length) return true
  
  const hotelAmenities = hotel.hotel_amenities || []
  const hotelAmenityCodes = hotelAmenities.map((amenity: any) => 
    typeof amenity === 'number' ? amenity : amenity.code || amenity.id
  )
  
  // Check if hotel has all required amenities
  return requiredCodes.every(code => hotelAmenityCodes.includes(code))
}

export function useClientSideFilters() {
  const route = useRoute()
  const searchStore = useSearchStore()

  // Get filter values from route query
  const filters = computed(() => ({
    // Hotel name filter
    hotelName: (route.query.hotel_name as string) || '',
    
    // Cancellation filter
    freeCancellation: route.query.free_cancellation === 'true',
    
    // Board plan filter
    boardPlans: route.query.board_plans ? (route.query.board_plans as string).split(',').filter(Boolean) : [],
    
    // Payment timing filters
    payAtPropertyOnly: route.query.pay_at_property_only === 'true',
    payNowOnly: route.query.pay_now_only === 'true',
    
    // Review quality filters
    minRating: route.query.min_rating ? parseFloat(route.query.min_rating as string) : 0,
    minReviews: route.query.min_reviews ? parseInt(route.query.min_reviews as string) : 0,
    
    // Location filters
    distanceBand: (route.query.distance_band as string) || '',
    neighborhoods: route.query.neighborhoods ? (route.query.neighborhoods as string).split(',').filter(Boolean) : [],
    customLocation: route.query.custom_location ? (() => {
      try {
        return JSON.parse(route.query.custom_location as string)
      } catch {
        return null
      }
    })() : null,
    
    // Amenity combo filters
    amenityCombos: route.query.amenity_combos ? (route.query.amenity_combos as string).split(',').filter(Boolean) : []
  }))

  // Map amenity combos to required amenity codes
  const requiredAmenityCodes = computed(() => {
    const amenityCombos = [
      {
        id: 'family-friendly',
        amenityCodes: [325, 665, 511] // Family Rooms, Pool, Laundry
      },
      {
        id: 'business-traveler',
        amenityCodes: [989, 366, 11] // Wi-Fi, GYM, 24-Hour Front Desk
      },
      {
        id: 'luxury-stay',
        amenityCodes: [837, 366, 729, 632] // Spa, GYM, Restaurant, Parking
      },
      {
        id: 'budget-essentials',
        amenityCodes: [989, 632, 551] // Wi-Fi, Parking, Luggage Storage
      },
      {
        id: 'pet-friendly',
        amenityCodes: [1009, 372, 632] // Pets Allowed, Garden, Parking
      },
      {
        id: 'accessible-stay',
        amenityCodes: [971, 11, 41] // Wheelchair-Accessible, 24-Hour Front Desk, Airport Shuttle
      },
      {
        id: 'wellness-focused',
        amenityCodes: [837, 366, 617] // Spa, Fitness Center, Outdoor Pool
      },
      {
        id: 'extended-stay',
        amenityCodes: [494, 511, 989] // Kitchen, Laundry, Wi-Fi
      }
    ]

    const allRequiredCodes = new Set<number>()
    
    filters.value.amenityCombos.forEach(comboId => {
      const combo = amenityCombos.find(c => c.id === comboId)
      if (combo) {
        combo.amenityCodes.forEach(code => allRequiredCodes.add(code))
      }
    })
    
    return Array.from(allRequiredCodes)
  })

  const applyClientSideFilters = (hotels: any[]) => {
    return hotels.filter(hotelItem => {
      const hotel = hotelItem.hotel || hotelItem
      const cheapestRate = hotelItem.cheapest_rate || hotel.rates?.[0]
      
      // No brand/chain filtering - removed as requested
      
      if (filters.value.hotelName) {
        const hotelName = hotel.hotel_name || hotel.name || ''
        if (!hotelName.toLowerCase().includes(filters.value.hotelName.toLowerCase())) {
          return false
        }
      }
      
      // Free cancellation filter
      if (filters.value.freeCancellation && cheapestRate) {
        if (!hasValidFreeCancellation(cheapestRate)) {
          return false
        }
      }
      
      // Board plan filter
      if (filters.value.boardPlans.length > 0 && cheapestRate) {
        const rateBoardPlan = cheapestRate.room_board?.description?.trim()
        if (!rateBoardPlan || !filters.value.boardPlans.some(plan => 
          rateBoardPlan.toLowerCase().includes(plan.toLowerCase())
        )) {
          return false
        }
      }
      
      // Payment timing filters
      if ((filters.value.payAtPropertyOnly || filters.value.payNowOnly) && cheapestRate) {
        if (!matchesPaymentPreference(cheapestRate, filters.value.payAtPropertyOnly, filters.value.payNowOnly)) {
          return false
        }
      }
      
      // Review quality filters
      if (filters.value.minRating > 0 || filters.value.minReviews > 0) {
        if (!meetsQualityRequirements(hotel, filters.value.minRating, filters.value.minReviews)) {
          return false
        }
      }
      
      // Location filters
      if (filters.value.distanceBand || filters.value.neighborhoods.length > 0 || filters.value.customLocation) {
        if (!matchesLocationPreference(hotel, filters.value.distanceBand, filters.value.neighborhoods, filters.value.customLocation)) {
          return false
        }
      }
      
      // Amenity combo filters
      if (requiredAmenityCodes.value.length > 0) {
        if (!hasRequiredAmenities(hotel, requiredAmenityCodes.value)) {
          return false
        }
      }
      
      return true
    })
  }

  // Apply filters to first page hotels
  const filteredHotelProductsFirstPage = computed(() => {
    const firstPage = searchStore.hotelProductsFirstPage || []
    return firstPage.map(chunk => applyClientSideFilters(chunk))
  })

  // Apply filters to regular hotels products
  const filteredHotelsProducts = computed(() => {
    const products = searchStore.hotelsProducts || []
    return applyClientSideFilters(products)
  })

  // Check if any client-side filters are active
  const hasClientSideFilters = computed(() => {
    return filters.value.hotelName.length > 0 ||
           filters.value.freeCancellation ||
           filters.value.boardPlans.length > 0 ||
           filters.value.payAtPropertyOnly ||
           filters.value.payNowOnly ||
           filters.value.minRating > 0 ||
           filters.value.minReviews > 0 ||
           filters.value.distanceBand.length > 0 ||
           filters.value.neighborhoods.length > 0 ||
           filters.value.amenityCombos.length > 0 ||
           filters.value.customLocation !== null
  })

  return {
    filteredHotelProductsFirstPage,
    filteredHotelsProducts,
    hasClientSideFilters,
    filters
  }
}